// Orders ShipStation gave us that belong to no client.
//
// Attribution is per-STORE (shipstation_client_map), but ShipStation's
// built-in Manual Orders store is shared across every client, so a hand-keyed
// order can never be attributed by mapping. sync.py used to discard these
// outright — no orders row, no fee, and only a soft warning that deliberately
// keeps the sync health marker green. They are parked now, and this is where
// they get a client.
//
// Assigning creates the real orders row with the TRUE ShipStation id, so it
// dedupes against any later sync. Fees are left to the scheduled reprice
// rather than typed here — hand-priced rows are locked out of repricing.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PackageSearch, Loader2, Check, AlertTriangle, RefreshCw } from 'lucide-react'
import { getUnassignedOrders, assignUnassignedOrder, getAdminClients, rescanUnassignedOrders, probeShipStationOrder, dismissUnassignedOrder, restoreUnassignedOrder } from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

function fmtDate(v) {
  if (!v) return '—'
  try {
    return new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: '2-digit',
    })
  } catch { return String(v) }
}

export default function UnassignedOrders() {
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()
  // client_id chosen per row, keyed by row id — the dropdown is the assignment.
  const [choice, setChoice] = useState({})
  const [showDismissed, setShowDismissed] = useState(false)
  const [probeNum, setProbeNum] = useState('')
  const [probeResult, setProbeResult] = useState(null)

  // `error` is destructured deliberately. `data: rows = []` makes a FAILED
  // request indistinguishable from an empty queue, so the page rendered a green
  // "Nothing waiting — every order has been attributed" while the request was
  // actually 502-ing because the unassigned_orders table did not exist. A
  // reassuring all-clear on an error is worse than showing nothing at all.
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['unassigned-orders', showDismissed],
    queryFn: () => getUnassignedOrders(false, showDismissed),
  })
  const { data: clientsData } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })
  const clients = (clientsData?.clients || clientsData || [])
    .filter(c => !c.archived)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  const assignMut = useMutation({
    mutationFn: ({ rowId, clientId }) => assignUnassignedOrder(rowId, clientId),
    onSuccess: () => {
      addToast({
        type: 'success',
        message: 'Assigned. It will appear in that client’s orders; fees are priced on the next reprice.',
      })
      queryClient.invalidateQueries({ queryKey: ['unassigned-orders'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Assign failed.',
    }),
  })

  // The sync is incremental with a 24h overlap, so an order processed while
  // this queue was broken falls behind the window and is NEVER reconsidered —
  // it just stays missing, with the page showing an honest but useless
  // "nothing waiting". This re-scans a bounded 14-day window to recover those.
  const rescan = useMutation({
    mutationFn: () => rescanUnassignedOrders(14),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['unassigned-orders'] })
      addToast({
        type: res?.staged ? 'success' : 'info',
        message: res?.note || 'Re-scan complete.',
      })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Re-scan failed.',
    }),
  })

  // Not a delete: the staging upsert would re-insert a deleted row on the next
  // scan, so the decision has to be recorded on the row itself.
  const dismissMut = useMutation({
    mutationFn: ({ rowId, reason }) => dismissUnassignedOrder(rowId, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['unassigned-orders'] })
      addToast({
        type: 'success',
        message: `Order ${res?.order_number || ''} dismissed — not billed to anyone.`,
      })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Dismiss failed.',
    }),
  })

  const restoreMut = useMutation({
    mutationFn: (rowId) => restoreUnassignedOrder(rowId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['unassigned-orders'] })
      addToast({ type: 'success', message: `Order ${res?.order_number || ''} is back in the queue.` })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Restore failed.',
    }),
  })

  // Read-only lookup against ShipStation for ONE order number.
  const probe = useMutation({
    mutationFn: () => probeShipStationOrder(probeNum.trim()),
    onSuccess: (res) => setProbeResult(res),
    onError: (err) => {
      setProbeResult(null)
      addToast({
        type: 'error',
        message: err.response?.data?.detail || 'Lookup failed.',
      })
    },
  })

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <div className="flex items-start justify-between gap-4">
        <div>
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <PackageSearch size={20} className="text-slate-500" />
          Unassigned Orders
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Orders whose ShipStation store maps to no client — normally the shared
          Manual Orders store. Pick a client to bring one into that client&rsquo;s billing.
        </p>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none shrink-0">
          <input
            type="checkbox"
            checked={showDismissed}
            onChange={e => setShowDismissed(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show dismissed
        </label>
        <button
          type="button"
          onClick={() => rescan.mutate()}
          disabled={rescan.isPending}
          className="btn-secondary shrink-0 whitespace-nowrap"
          title="Re-scan the last 14 days of ShipStation orders. Use this when an order you can see in ShipStation never appeared here — the routine sync only looks back 24 hours, so anything missed during an outage is never revisited."
        >
          {rescan.isPending
            ? <Loader2 size={14} className="animate-spin" />
            : <RefreshCw size={14} />}
          {rescan.isPending ? 'Re-scanning…' : 'Re-scan last 14 days'}
        </button>
      </div>

      {/* Settles the one question the queue itself can never answer: is the
          order absent because ShipStation has it unshipped (invisible to a
          sync that only ever asks for shipped orders), or because we missed
          it? Read-only. */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 shrink-0">
          Order missing? Ask ShipStation directly:
        </span>
        <input
          type="text"
          value={probeNum}
          onChange={e => setProbeNum(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && probeNum.trim()) probe.mutate() }}
          placeholder="Order number, e.g. 100035"
          className="input w-56 text-sm"
        />
        <button
          type="button"
          onClick={() => probe.mutate()}
          disabled={probe.isPending || !probeNum.trim()}
          className="btn-secondary"
        >
          {probe.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
          {probe.isPending ? 'Checking…' : 'Check'}
        </button>
        {probeResult && (
          <div className="w-full mt-1 text-xs">
            <p className="text-slate-700">{probeResult.verdict}</p>
            {(probeResult.matches || []).map(m => (
              <p key={m.shipstation_order_id} className="text-slate-500 mt-1 font-mono break-all">
                {m.order_number} · status <b>{m.order_status || '—'}</b> · store {m.store_id || '—'}
                {m.store_mapped_to ? ` → ${m.store_mapped_to}` : ' → unmapped'}
                {m.tracking_number ? ` · ${m.tracking_number}` : ' · no tracking'}
                {` · in orders: ${m.in_orders_table ? 'yes' : 'no'} · in queue: ${m.in_queue ? 'yes' : 'no'}`}
              </p>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-blue-600" />
        </div>
      ) : error ? (
        /* Never claim the queue is clear when we could not read it. */
        <div className="card p-10 text-center">
          <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
          <p className="text-sm font-medium text-slate-700">
            Couldn't load the queue.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            This is <b>not</b> the same as "nothing waiting" — there may be orders
            parked here that we cannot see.
          </p>
          <p className="text-xs text-red-600 mt-2 font-mono break-all">
            {error?.response?.data?.detail || error?.message || 'Unknown error'}
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center">
          <Check size={28} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-sm font-medium text-slate-700">Nothing waiting.</p>
          <p className="text-xs text-slate-500 mt-1">
            Every order ShipStation sent us has been attributed to a client.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Ship date', 'Order #', 'Store', 'Recipient', 'Carrier', 'Tracking', 'Assign to client', ''].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => {
                  const picked = choice[r.id] || ''
                  const busy = assignMut.isPending && assignMut.variables?.rowId === r.id
                  return (
                    <tr
                      key={r.id}
                      className={`hover:bg-slate-50 transition-colors ${r.dismissed_at ? 'opacity-50' : ''}`}
                      title={r.dismissed_at
                        ? `Dismissed${r.dismiss_reason ? ` — ${r.dismiss_reason}` : ''}. Not billed to anyone.`
                        : undefined}
                    >
                      <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 text-xs">{fmtDate(r.ship_date)}</td>
                      <td className="px-3 py-2.5 font-mono text-xs font-medium text-slate-800">{r.order_number || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{r.store_name || r.store_id || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">
                        {r.recipient_name || '—'}
                        {r.recipient_state && <span className="text-slate-400 ml-1">({r.recipient_state})</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{r.carrier || '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{r.tracking_number || '—'}</td>
                      <td className="px-3 py-2.5">
                        <select
                          value={picked}
                          onChange={e => setChoice(c => ({ ...c, [r.id]: e.target.value }))}
                          className="input text-sm py-1 min-w-[200px]"
                          disabled={busy}
                        >
                          <option value="">Select a client…</option>
                          {clients.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={() => {
                            const client = clients.find(c => c.id === picked)
                            if (!client) return
                            if (!window.confirm(
                              `Assign order ${r.order_number || ''} to ${client.name}?\n\n` +
                              `This creates a real order on their account. Fees are priced ` +
                              `from their rate card on the next reprice.`
                            )) return
                            assignMut.mutate({ rowId: r.id, clientId: picked })
                          }}
                          disabled={!picked || busy}
                          className="btn-primary text-xs py-1 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busy ? <Loader2 size={13} className="animate-spin" /> : 'Assign'}
                        </button>
                        {r.dismissed_at ? (
                          <button
                            onClick={() => restoreMut.mutate(r.id)}
                            disabled={restoreMut.isPending}
                            className="btn-secondary text-xs py-1 px-2 ml-2"
                            title="Put this back in the queue"
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              const reason = window.prompt(
                                `Dismiss order ${r.order_number || ''} to ${r.recipient_name || 'this recipient'}?\n\n` +
                                `It will not be billed to any client and will leave the queue. ` +
                                `Use this for parcels that are not client work — a personal ` +
                                `Australia Post shipment through the shared Manual Orders store, for example.\n\n` +
                                `Reason (optional):`,
                                ''
                              )
                              if (reason === null) return   // cancelled
                              dismissMut.mutate({ rowId: r.id, reason: reason.trim() })
                            }}
                            disabled={dismissMut.isPending}
                            className="text-xs py-1 px-2 ml-2 text-slate-500 hover:text-red-600"
                            title="Not client work — remove from the queue without billing anyone"
                          >
                            Dismiss
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="flex items-start gap-1.5 text-xs text-slate-400">
        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
        <span>
          An order lands here when its ShipStation store belongs to no client. If a whole
          store should map to one client, do that on the client instead — this queue is for
          the shared Manual Orders store, which serves everyone and so can never be mapped.
        </span>
      </p>
    </div>
  )
}
